import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from "../context/AuthProvider"
import leftImg from '../assets/img/Venlo-welcome.png'
import { PrelimFooter, PrelimHeader, SubmitButton } from '../exports'
import { MdOutlineMailOutline, MdLockOutline } from 'react-icons/md'
import { FaRegEyeSlash, FaRegEye } from 'react-icons/fa'
import './login.css'

function Login() {
    const { login, user, isLoading } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const from = location.state?.from?.pathname || "/dashboard"
    const [showPass, setShowPass] = useState(false)
    const [errors, setErrors] = useState({})
    const [Loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
        email: "",
        password: "",
    })

    //Auto redirect logged in users to dashboard
    useEffect(() => {
        if (!isLoading && user) {
            navigate(from, { replace: true })
        }
    }, [user, isLoading, navigate, from])

    //Password visibility toggle handler
    const showPassword = () => {
        setShowPass(prev => !prev)
    }

    const handleChange = (e) => {
        // Clear error for this field when user starts typing
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: "" })
        }
        setFormData({
            ...formData, 
            [e.target.name]: e.target.value
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const formErrors = {}
        
        if (!formData.email.trim()) {
            formErrors.email = "Email is required"
        } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
            formErrors.email = "Please enter a valid email address"
        }
        
        if (!formData.password.trim()) {
            formErrors.password = "Password is required"
        } else if (formData.password.length < 6) {
            formErrors.password = "Password must be at least 6 characters"
        }
        
        setErrors(formErrors)
        
        if (Object.keys(formErrors).length === 0) {
            setLoading(true)
            try {
                await login(formData.email, formData.password);
                navigate(from, { replace: true })
            } catch (err) {
                const data = err.response?.data

                // Unverified email — redirect straight to OTP page
                // Login endpoint already sent them a fresh OTP at this point
                if (data?.isEmailVerified === false) {
                    navigate('/email-auth', {
                        state: {
                            email: formData.email,
                            role:  data.role,        // from the updated backend response
                        }
                    })
                    return
                }  
                // Any other error — show inline message as before
                setErrors(prev => ({ ...prev, submit: data?.message || err.message || 'Login failed' }))
            } finally {
                setLoading(false)
            }
        }
    }

    return (
        <>
            <div className="login-page">
                <div className="login-container">
                    {/* Left Side - Hero Section */}
                    <div className="login-hero">
                        <div className="login-hero-content">
                            <img 
                                className="login-hero-image" 
                                src={leftImg} 
                                alt="Welcome to Venlorent" 
                            />
                            <div className="login-hero-text">
                                <h1 className="login-hero-title">
                                    Find it. Love it. Rent it.
                                </h1>
                                <p className="login-hero-subtitle">
                                    Skip the stress, find the best with Nigeria's most trusted property marketplace.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Login Form */}
                    <div className="login-form-section">
                        <div className="login-form-container">
                            <PrelimHeader pageTitle="Welcome back" />
                            
                            <form onSubmit={handleSubmit} className="login-form">
                                {/* Email Input */}
                                <div className="form-group">
                                    <label htmlFor="email" className="form-label">
                                        Email address
                                    </label>
                                    <div className="input-wrapper">
                                        <MdOutlineMailOutline className="input-icon-left" />
                                        <input
                                            id="email"
                                            className={`form-input ${errors.email ? 'input-error' : ''}`}
                                            type="text"
                                            name="email"
                                            placeholder="Enter your email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            autoComplete="email"
                                            disabled={Loading}
                                        />
                                    </div>
                                    {errors.email && (
                                        <span className="error-message">{errors.email}</span>
                                    )}
                                </div>

                                {/* Password Input */}
                                <div className="form-group">
                                    <label htmlFor="password" className="form-label">
                                        Password
                                    </label>
                                    <div className="input-wrapper">
                                        <MdLockOutline className="input-icon-left" />
                                        <input
                                            id="password"
                                            className={`form-input ${errors.password ? 'input-error' : ''}`}
                                            type={showPass ? "text" : "password"}
                                            name="password"
                                            placeholder="Enter your password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            autoComplete="current-password"
                                            disabled={Loading}
                                        />
                                        <button
                                            type="button"
                                            className="input-icon-right"
                                            onClick={showPassword}
                                            aria-label={showPass ? "Hide password" : "Show password"}
                                        >
                                            {showPass ? <FaRegEye /> : <FaRegEyeSlash />}
                                        </button>
                                    </div>
                                    {errors.password && (
                                        <span className="error-message">{errors.password}</span>
                                    )}
                                </div>

                                {/* Forgot Password Link */}
                                <div className="form-footer">
                                    <Link to="/password-recovery" className="forgot-password-link">
                                        Forgot Password?
                                    </Link>
                                </div>

                                {/* Submit Error */}
                                {errors.submit && (
                                    <div className="submit-error">
                                        {errors.submit}
                                    </div>
                                )}
                               
                                {/* Submit Button */}
                                <SubmitButton 
                                    text={Loading ? "Logging in..." : "Log In"} 
                                    disabled={Loading}
                                />

                                {/* Sign Up Link */}
                                <p className="signup-prompt">
                                    Don't have an account?{' '}
                                    <Link to="/register" className="signup-link">
                                        Sign up
                                    </Link>
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <PrelimFooter />
        </>
    )
}

export default Login
